import { useCallback, useState } from 'react';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
// Types
import { SortDirection } from 'api/graphql-global-types';

export type ServerTable<SortBy> = {
  page: number;
  pageSize: number;
  offset: number;
  sortBy: SortBy;
  direction: SortDirection;
  setPage: (page: number) => void;
  sortOrder: (columnKey: string) => SortOrder;
  onChange: <Row>(
    pagination: TablePaginationConfig,
    filters: unknown,
    sorter: SorterResult<Row> | SorterResult<Row>[]
  ) => void;
};

/** Page and sort state for an antd table whose rows the backend pages and sorts. */
export const useServerTable = <SortBy extends string>({
  sortColumns,
  defaultSortBy,
  defaultPageSize,
}: {
  /** Column key to the backend's sort field. */
  sortColumns: Record<string, SortBy>;
  defaultSortBy: SortBy;
  defaultPageSize: number;
}): ServerTable<SortBy> => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sort, setSort] = useState({
    sortBy: defaultSortBy,
    direction: SortDirection.DESC,
  });

  const onChange = useCallback(
    <Row>(
      pagination: TablePaginationConfig,
      _filters: unknown,
      sorter: SorterResult<Row> | SorterResult<Row>[]
    ): void => {
      const active = Array.isArray(sorter) ? sorter[0] : sorter;
      const sortBy = sortColumns[String(active?.columnKey)];

      // A cleared sort goes back to the default one
      setSort(
        sortBy && active.order
          ? {
              sortBy,
              direction:
                active.order === 'ascend'
                  ? SortDirection.ASC
                  : SortDirection.DESC,
            }
          : { sortBy: defaultSortBy, direction: SortDirection.DESC }
      );
      setPage(pagination.current || 1);
      setPageSize(pagination.pageSize || defaultPageSize);
    },
    [sortColumns, defaultSortBy, defaultPageSize]
  );

  const sortOrder = useCallback(
    (columnKey: string): SortOrder => {
      if (sortColumns[columnKey] !== sort.sortBy) {
        return null;
      }

      return sort.direction === SortDirection.ASC ? 'ascend' : 'descend';
    },
    [sortColumns, sort]
  );

  return {
    page,
    pageSize,
    offset: (page - 1) * pageSize,
    sortBy: sort.sortBy,
    direction: sort.direction,
    setPage,
    sortOrder,
    onChange,
  };
};
